/**
 * 학습 설정 에디터
 */
import { useTranslation } from 'react-i18next'
import { Lock } from 'lucide-react'
import type { LearningSettings, LearningSettingsOverrides } from '@/types/configuration'

interface LearningSettingsEditorProps {
  settings: LearningSettings
  onUpdate: (settings: LearningSettings) => void
  parentOverrides?: LearningSettingsOverrides
}

export const LearningSettingsEditor: React.FC<LearningSettingsEditorProps> = ({
  settings,
  onUpdate,
  parentOverrides
}) => {
  const { t } = useTranslation('configuration')
  const isFieldLocked = (field: keyof LearningSettingsOverrides) => parentOverrides?.[field] === false

  return (
    <div className="space-y-6">
      <h4 className="text-sm font-medium text-gray-700">{t('learningSettings.title')}</h4>

      <div className="space-y-4">
        {/* 대시보드 표시 */}
        <label className={`flex items-start gap-3 ${isFieldLocked('showDashboard') ? 'opacity-60' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            checked={settings.showDashboard}
            onChange={(e) => onUpdate({ ...settings, showDashboard: e.target.checked })}
            disabled={isFieldLocked('showDashboard')}
            className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
          />
          <div>
            <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
              {t('learningSettings.showDashboard')}
              {isFieldLocked('showDashboard') && <Lock className="w-3.5 h-3.5 text-gray-400" />}
            </span>
            <p className="text-xs text-gray-500 mt-0.5">
              {t('learningSettings.showDashboardDesc')}
            </p>
          </div>
        </label>

        {/* 진행률 표시 */}
        <label className={`flex items-start gap-3 ${isFieldLocked('showProgress') ? 'opacity-60' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            checked={settings.showProgress}
            onChange={(e) => onUpdate({ ...settings, showProgress: e.target.checked })}
            disabled={isFieldLocked('showProgress')}
            className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
          />
          <div>
            <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
              {t('learningSettings.showProgress')}
              {isFieldLocked('showProgress') && <Lock className="w-3.5 h-3.5 text-gray-400" />}
            </span>
            <p className="text-xs text-gray-500 mt-0.5">
              {t('learningSettings.showProgressDesc')}
            </p>
          </div>
        </label>

        {/* 학습 시간 추적 */}
        <label className={`flex items-start gap-3 ${isFieldLocked('trackLearningTime') ? 'opacity-60' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            checked={settings.trackLearningTime}
            onChange={(e) => onUpdate({ ...settings, trackLearningTime: e.target.checked })}
            disabled={isFieldLocked('trackLearningTime')}
            className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
          />
          <div>
            <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
              {t('learningSettings.trackLearningTime')}
              {isFieldLocked('trackLearningTime') && <Lock className="w-3.5 h-3.5 text-gray-400" />}
            </span>
            <p className="text-xs text-gray-500 mt-0.5">
              {t('learningSettings.trackLearningTimeDesc')}
            </p>
          </div>
        </label>

        {/* 노트 기능 */}
        <label className={`flex items-start gap-3 ${isFieldLocked('enableNotes') ? 'opacity-60' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            checked={settings.enableNotes}
            onChange={(e) => onUpdate({ ...settings, enableNotes: e.target.checked })}
            disabled={isFieldLocked('enableNotes')}
            className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
          />
          <div>
            <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
              {t('learningSettings.enableNotes')}
              {isFieldLocked('enableNotes') && <Lock className="w-3.5 h-3.5 text-gray-400" />}
            </span>
            <p className="text-xs text-gray-500 mt-0.5">
              {t('learningSettings.enableNotesDesc')}
            </p>
          </div>
        </label>

        {/* 북마크 기능 */}
        <label className={`flex items-start gap-3 ${isFieldLocked('enableBookmarks') ? 'opacity-60' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            checked={settings.enableBookmarks}
            onChange={(e) => onUpdate({ ...settings, enableBookmarks: e.target.checked })}
            disabled={isFieldLocked('enableBookmarks')}
            className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
          />
          <div>
            <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
              {t('learningSettings.enableBookmarks')}
              {isFieldLocked('enableBookmarks') && <Lock className="w-3.5 h-3.5 text-gray-400" />}
            </span>
            <p className="text-xs text-gray-500 mt-0.5">
              {t('learningSettings.enableBookmarksDesc')}
            </p>
          </div>
        </label>
      </div>

      {/* 설명 박스 */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h5 className="text-sm font-medium text-gray-700 mb-2">{t('learningSettings.infoTitle')}</h5>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>• {t('learningSettings.infoScope')}</li>
          <li>• {t('learningSettings.infoDashboard')}</li>
          <li>• {t('learningSettings.infoData')}</li>
        </ul>
      </div>
    </div>
  )
}
